import { useEffect, useState } from "react";
import { toast } from "sonner";
import { ShieldWarning, CheckCircle, ChatCircleDots } from "@phosphor-icons/react";
import { DashboardLayout } from "../components/layout/DashboardLayout";
import { api, formatApiErrorDetail } from "../lib/api";
import { useEditConfirm } from "../context/EditConfirmContext";

const navItems = [{ to: "/admin/compliance-inbox", label: "Compliance Inbox", icon: <ShieldWarning size={18} weight="duotone" />, testId: "nav-admin-compliance-inbox" }];

export default function AdminComplianceInboxPage() {
  const confirmEdit = useEditConfirm();
  const [entries, setEntries] = useState(null);
  const [flaggedOnly, setFlaggedOnly] = useState(true);

  const load = () => {
    api.get(`/admin/conversation-archive?flagged_only=${flaggedOnly}`)
      .then(({ data }) => setEntries(data))
      .catch((e) => toast.error(formatApiErrorDetail(e.response?.data?.detail)));
  };

  useEffect(load, [flaggedOnly]);

  const markReviewed = async (id) => {
    if (!(await confirmEdit("this conversation's reviewed status"))) return;
    try {
      await api.patch(`/admin/conversation-archive/${id}`, { reviewed: true });
      toast.success("Marked reviewed");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  return (
    <DashboardLayout navItems={navItems} moduleName="Compliance Inbox" moduleBackPath="/admin">
      <div data-testid="admin-compliance-inbox-page">
        <div className="flex items-center justify-between mb-2">
          <h1 className="font-serif text-3xl text-igc-cream tracking-tight" title="Every conversation Nia (the AI assistant) has had that touched a sensitive keyword like lawsuit, refund, or complaint — for compliance review.">Nia Compliance Inbox</h1>
          <div className="flex gap-2">
            <button data-testid="compliance-filter-flagged" onClick={() => setFlaggedOnly(true)} title="Show only conversations Nia auto-flagged as compliance-sensitive." className={`px-3 py-1.5 text-xs uppercase tracking-wide transition-colors ${flaggedOnly ? "bg-igc-gold text-black" : "border border-igc-gold/30 text-igc-goldText"}`}>Flagged Only</button>
            <button data-testid="compliance-filter-all" onClick={() => setFlaggedOnly(false)} title="Show every Nia conversation, flagged or not." className={`px-3 py-1.5 text-xs uppercase tracking-wide transition-colors ${!flaggedOnly ? "bg-igc-gold text-black" : "border border-igc-gold/30 text-igc-goldText"}`}>All Conversations</button>
          </div>
        </div>
        <p className="text-igc-muted text-sm mb-8">Every Nia exchange that touches a compliance-sensitive keyword (lawsuit, refund, complaint, etc.) lands here automatically for review.</p>

        {!entries ? null : entries.length === 0 ? (
          <p className="text-igc-muted text-sm" data-testid="admin-compliance-empty">Nothing here — Nia hasn't flagged anything.</p>
        ) : (
          <div className="space-y-3" data-testid="admin-compliance-list">
            {entries.map((e) => (
              <div key={e.id} className={`bg-igc-surface border p-4 ${e.flagged && !e.reviewed ? "border-igc-danger/40" : "border-igc-gold/15"}`} data-testid={`admin-compliance-row-${e.id}`}>
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <ChatCircleDots size={16} weight="duotone" className="text-igc-goldText" />
                    <span className="text-igc-cream text-sm">{e.user_display_name}</span>
                    <span className="text-igc-muted text-xs capitalize">({e.user_role})</span>
                    {e.flagged && <span className="text-[10px] uppercase tracking-wide px-1.5 py-0.5 bg-igc-danger/15 text-igc-danger">Flagged: {e.flag_reason}</span>}
                  </div>
                  <span className="text-igc-muted text-xs">{e.created_at ? new Date(e.created_at).toLocaleString() : ""}</span>
                </div>
                <p className="text-igc-muted text-xs mb-1">Q: {e.question}</p>
                <p className="text-igc-cream text-sm mb-3">A: {e.answer}</p>
                {e.flagged && !e.reviewed && (
                  <button data-testid={`admin-compliance-mark-reviewed-${e.id}`} onClick={() => markReviewed(e.id)} title="Marks this flagged conversation as reviewed by you — it stays in the log but no longer needs attention." className="flex items-center gap-1.5 text-xs text-igc-success hover:text-igc-success/80">
                    <CheckCircle size={14} weight="bold" /> Mark Reviewed
                  </button>
                )}
                {e.reviewed && <span className="text-igc-success text-xs">Reviewed</span>}
              </div>
            ))}
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}
